import { useState, useCallback } from 'react'
import { speechRecognitionService } from '../services/speechRecognitionService'

/**
 * Hook for speech recognition functionality
 * Handles API calls and result processing
 */
export const useSpeechRecognition = () => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [extractedData, setExtractedData] = useState({})
  const [confidence, setConfidence] = useState(null)
  const [error, setError] = useState(null)

  /**
   * Send audio for recognition and extract data
   * @param {string} audioBase64 - Base64 encoded audio data
   * @param {number} satsueiMemoCategoryId - Category ID for field mapping
   * @param {object} options - Optional configuration
   * @returns {Promise<object>} Recognition result
   */
  const recognizeSpeech = useCallback(async (audioBase64, satsueiMemoCategoryId, options = {}) => {
    setIsProcessing(true)
    setError(null)
    setTranscript('')
    setExtractedData({})
    setConfidence(null)

    try {
      const result = await speechRecognitionService.recognizeSpeech(
        audioBase64,
        satsueiMemoCategoryId,
        options
      )

      if (result.returnCode !== '200') {
        throw new Error(result.message || '音声認識に失敗しました')
      }

      setTranscript(result.transcript || '')
      setExtractedData(result.extractedData || {})
      setConfidence(result.confidence)

      return {
        transcript: result.transcript,
        extractedData: result.extractedData,
        confidence: result.confidence
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || '音声認識に失敗しました'
      setError(errorMessage)
      throw err
    } finally {
      setIsProcessing(false)
    }
  }, [])

  /**
   * Clear recognition results
   */
  const clearResults = useCallback(() => {
    setTranscript('')
    setExtractedData({})
    setConfidence(null)
    setError(null)
  }, [])

  /**
   * Get predefined voice keywords
   * @returns {Promise<object>} Map of keyword to description
   */
  const getVoiceKeywords = useCallback(async () => {
    try {
      return await speechRecognitionService.getVoiceKeywords()
    } catch (err) {
      console.error('Failed to get voice keywords:', err)
      throw err
    }
  }, [])

  return {
    isProcessing,
    transcript,
    extractedData,
    confidence,
    error,
    recognizeSpeech,
    clearResults,
    getVoiceKeywords
  }
}

export default useSpeechRecognition
